import { toast } from 'react-toastify';
import {
  BITA_WITHHOLDING_TAX,
  ERROR_MESSAGES_MAP,
  TYPE_OF_MODIFICATION,
  FIELDS_TO_MODULES_MAP,
  FIELDS_TO_PROPERTIES_MAP,
  DATA_CALCULATION_SETUP_VALUES,
  DAYS_OF_THE_WEEK,
  EXCLUDED_ITEMS,
  SUMMARY_BULK_PREVIEW_LABELS,
  CALCULATION_FREQUENCY_MAPPING,
  AT_MARKET_OPEN,
  PRICE_RETURN,
  CASH_DISTRIBUTION_APPLICATION_POINT_LABEL,
  CASH_DISTRIBUTION_TYPE_LABEL,
} from './constants';
import {
  consultExistBasket,
  getBasket,
  getBasketById,
  getWithholdingTaxTemplates,
  downloadTemplate,
  recalculateCoverage,
  updateBulkBasket,
  createBulkBasket,
  getCalculationZones,
} from './api';
import { toastInterceptor } from '../../../utils/utils';
import { ORGANIZATION_PREFIXES } from '../../../constants/organizationPrefixConstants';

// Private
const truncateFloat = value => Math.trunc(value * 100) / 100; // Truncate to two decimal places

export const parseDecimalInputValue = value => {
  const cleanValue = value.replace(/,/g, '');
  return cleanValue.endsWith('.') ? cleanValue : Number(cleanValue);
};

// Public
export const isFloatWithMoreThanTwoDecimals = value => {
  try {
    // Check if the value is a number and has a decimal part
    if (typeof value === 'number' && !Number.isInteger(value)) {
      const decimalPart = value % 1; // Extract the decimal part

      const numberOfDecimals = decimalPart.toString().split('.')[1]?.length; // Count the number of decimals by converting to string and splitting

      if (numberOfDecimals > 2) {
        // If more than two decimals, truncate and return the truncated value
        return truncateFloat(value);
      }
    }

    return false; // Return false for non-numeric values or integers, or for values with two or fewer decimals
  } catch (error) {
    return false; // If an error occurs, just return false by default
  }
};

export const orderByType = arr => {
  return arr.reduce((acc, obj) => {
    if (obj.type === TYPE_OF_MODIFICATION.WITH_COVERAGE) {
      acc.push(obj);
    } else {
      acc.unshift(obj);
    }
    return acc;
  }, []);
};

export const existRequestCoverageWithData = data => {
  return data?.some(item => item?.type !== TYPE_OF_MODIFICATION.WITH_COVERAGE) ?? false;
};

export const buildRequestCoverageBody = ({
  data,
  user,
  withCoverageType = TYPE_OF_MODIFICATION.WITH_COVERAGE,
}) => {
  let body;

  const mapCoverageItems = items => {
    return (items || []).map(coverage => {
      return {
        sedol: coverage?.sedol ?? null,
        isin: coverage?.isin ?? null,
        mic: coverage?.exchange ?? null,
        type: coverage?.type ?? null,
      };
    });
  };

  const identifierType = data?.identifier_type;

  if (identifierType) {
    if (identifierType === 'sedol') {
      const sedolsArray = data?.meta_reference_data?.filter(
        item => item?.type !== withCoverageType,
      );

      const sedols = mapCoverageItems(sedolsArray);
      body = {
        user_email: user?.userdata?.email,
        user_name: user?.username,
        company_name: user?.company,
        request_coverage: sedols,
      };
    } else if (identifierType === 'isin') {
      const isinArray = data?.meta_reference_data?.filter(item => item?.type !== withCoverageType);
      const isins = mapCoverageItems(isinArray);
      body = {
        user_email: user?.userdata?.email,
        user_name: user?.username,
        company_name: user?.company,
        request_coverage: isins,
      };
    }
  } else {
    const instrumentsArray = data?.meta_reference_data?.filter(
      item => item?.type !== withCoverageType,
    );
    const instruments = mapCoverageItems(instrumentsArray);
    body = {
      user_email: user?.userdata?.email,
      user_name: user?.username,
      company_name: user?.company,
      request_coverage: instruments,
    };
  }

  return body;
};

const formatDateAsYYYYMMDD = date =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;

export const getDateString = (offsetDays = 0) => {
  const date = new Date();
  date.setDate(date.getDate() + offsetDays);
  date.setHours(0, 0, 0, 0);
  return formatDateAsYYYYMMDD(date);
};

export const generateEffectiveDate = calculationSetup => {
  if (calculationSetup && calculationSetup === 'EOD') {
    return getDateString(1);
  }
  return getDateString();
};

export const isDateBeforeOrToday = date => {
  try {
    if (!date) return false;
    const inputDate = new Date(date);
    if (Number.isNaN(inputDate.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    inputDate.setHours(0, 0, 0, 0);
    return inputDate <= today;
  } catch (error) {
    return false;
  }
};

export const sortByKey = (arr, key, order = 'asc') => {
  if (!Array.isArray(arr)) return [];
  return [...arr].sort((a, b) => {
    if (a[key] < b[key]) return order === 'asc' ? -1 : 1;
    if (a[key] > b[key]) return order === 'asc' ? 1 : -1;
    return 0;
  });
};

// Verifies that a date is from today or later (and not a date in the past). For example: valid dates: today date, tomorrow date. Invalid dates: yesterday date, last month date, etc.
export const isValidDateString = dateString => {
  // Check if the date string matches the format yyyy-mm-dd
  const regex = /^\d{4}-\d{2}-\d{2}$/;
  if (!regex.test(dateString)) {
    return false;
  }

  // Parse the date string into a Date object
  const date = new Date(`${dateString}T00:00:00`); // Treat as local midnight
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Normalize today's date to local midnight

  // Check if the date is today or in the future
  return date >= today;
};

export const isValidDateFormat = dateString => {
  let isValid = false;
  const regex = /^\d{4}-\d{2}-\d{2}$/;

  if (!regex.test(dateString)) {
    isValid = false;
  }

  const date = new Date(dateString);
  isValid = date.toISOString().slice(0, 10) === dateString;

  return isValid;
};

// Helper to remove null or undefined values from an object
export const removeNulls = obj =>
  Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== null && v !== undefined));

export const generateLaunchDate = date => {
  if (!date) return getDateString();
  if (isValidDateFormat(date) && isValidDateString(date)) {
    return date;
  }
  return getDateString();
};

/**
 * Helper to extract the symbols of tickers that exist from an existence check results array.
 * @param {Array} existenceChecks - Array of objects with {symbol, exists, ...}
 * @returns {Array<string>} Array of ticker symbols that exist
 */
export const filterExistingTickersAndReturnSymbols = existenceChecks => {
  return existenceChecks.filter(res => res.exists).map(res => res.symbol);
};

/**
 * Checks for the existence of tickers in bulk.
 * @param {Array} baskets - Array of basket objects to check.
 * @param {string} token - Auth token to use for API calls.
 * @param {function} consultExistBasket - API function that checks ticker existence.
 * @param {function} [toast] - Optional toast function for errors.
 * @returns {Promise<{existingTickers: string[], errors: any[]}>} - Existing tickers and any errors
 */
export const checkTickersExistence = async ({ baskets, token }) => {
  try {
    const existenceChecks = await Promise.all(
      baskets.map(async basket => {
        const fullTicker = (basket.org_prefix || '') + basket.symbol;
        try {
          const rsp = await consultExistBasket(token, fullTicker);
          return {
            symbol: fullTicker,
            exists: rsp.data?.data?.length > 0,
            error: false,
          };
        } catch (err) {
          return {
            symbol: fullTicker,
            exists: false,
            error: true,
          };
        }
      }),
    );
    return filterExistingTickersAndReturnSymbols(existenceChecks);
  } catch (err) {
    const existenceChecks = baskets.map(basket => ({
      symbol: (basket.org_prefix || '') + basket.symbol,
      exists: false,
      error: true,
    }));
    return filterExistingTickersAndReturnSymbols(existenceChecks);
  }
};

export const showTickersExistenceError = existingTickers => {
  toast.error(
    `The following tickers are already in use: ${existingTickers.join(
      ', ',
    )}. Please choose different tickers.`,
  );
};

/**
 * Checks if a basket symbol already exists (excluding the old symbol for updates).
 * @param {string} newSymbol - The new symbol to check.
 * @param {string} oldSymbol - The old symbol to ignore (in case of an update).
 * @returns {boolean} true if symbol exists, false otherwise.
 */
export const doesSymbolExist = (newSymbol = '', selectedBasketsData) => {
  const normalizedNewSymbol = (newSymbol || '').trim().toUpperCase();
  const symbolExists = selectedBasketsData?.some(basket => {
    const basketSymbol = (basket.symbol || '').trim().toUpperCase();
    return basketSymbol === normalizedNewSymbol;
  });
  if (symbolExists) {
    return true;
  }
  return false;
};

export const getBaskets = async (token, setAllBasketsList) => {
  try {
    const rsp = await getBasket(token);
    const sortedBaskets = sortByKey(Object.values(rsp?.data?.data), 'symbol', 'asc');
    setAllBasketsList(sortedBaskets);
  } catch (error) {
    toast.error('Error fetching baskets');
  }
};

export const handleError = error => {
  const normalizedError = error.response?.data?.error ?? '';

  const friendlyError =
    ERROR_MESSAGES_MAP[normalizedError] ||
    (normalizedError.includes(`Format error in 'divisor': could not convert string to float:`)
      ? 'Format error in the ‘divisor’ field: the value entered is not a valid number.'
      : normalizedError);

  return {
    ...error,
    response: {
      ...error.response,
      data: {
        ...error.response.data,
        error: friendlyError,
      },
    },
  };
};

export const sortWithholdingTaxTemplates = (templates = []) => {
  // Use spread to avoid mutating the original array and handle undefined input
  return [...templates].sort((a, b) => {
    const nameA = (a?.withholding_tax_name ?? '').toUpperCase();
    const nameB = (b?.withholding_tax_name ?? '').toUpperCase();
    return nameA.localeCompare(nameB);
  });
};

export const updateWithholdingTaxTemplatesData = async (user, setWithholdingTaxTemplateData) => {
  try {
    const rsp = await getWithholdingTaxTemplates(user);
    const dataTax = [...rsp?.data?.data, { ...BITA_WITHHOLDING_TAX, id: 'default' }];
    const sortedDataTax = sortWithholdingTaxTemplates(dataTax);
    setWithholdingTaxTemplateData(sortedDataTax);
  } catch (error) {
    toastInterceptor(error);
  }
};

const showSymbolAssignmentMessage = (symbol, fileName) => {
  if (fileName) {
    toast.error(`No symbol found in "${fileName}". Assigned: "${symbol}".`);
  }
};

/**
 * Generates a unique symbol for baskets without a symbol.
 * Pattern: TICKER, TICKER1, TICKER2, TICKER3, etc.
 * @param {Set<string>} existingSymbols - Set of existing symbols to avoid duplicates
 * @param {Map<string, any>} symbolToFile - Map of symbols already processed in current upload
 * @param {Set<string>} prevUploadedSymbols - Set of previously uploaded symbols
 * @param {string} fileName - Name of the uploaded file
 */
export const generateUniqueSymbol = (
  existingSymbols,
  symbolToFile,
  prevUploadedSymbols,
  fileName,
) => {
  const baseSymbol = 'TICKER';
  const allSymbols = new Set([
    ...existingSymbols,
    ...Array.from(symbolToFile.keys()),
    ...Array.from(prevUploadedSymbols),
  ]);

  // Prefer base symbol if available
  if (!allSymbols.has(baseSymbol)) {
    showSymbolAssignmentMessage(baseSymbol, fileName);
    return baseSymbol;
  }
  const newSymbol = Array.from({ length: allSymbols.size + 2 })
    .map((_, i) => `${baseSymbol}${i === 0 ? 1 : i + 1}`)
    .find(symbol => !allSymbols.has(symbol));
  showSymbolAssignmentMessage(newSymbol, fileName);
  return newSymbol;
};

export const handleDownloadTemplate = async (user, data) => {
  const body = {
    data: [
      {
        symbol: data?.symbol,
        calculation_setup: data?.calculation_setup,
        effective_date: generateEffectiveDate(data?.calculation_setup),
      },
    ],
  };

  try {
    const response = await downloadTemplate(user.token, body);
    const blob = response.data;
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', `Template-Basket-${data?.symbol}-${getDateString()}.zip`);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    toast.error(
      'Failed to download template. Please check your network connection or try again later.',
    );
  }
};

/**
 * Handles the bulk download of templates for multiple baskets.
 * @param {Object} params - Parameters object
 * @param {Array} params.allBasketsList - Array of all available baskets
 * @param {Array} params.checkedBasketsIds - Array of selected basket IDs to download
 * @param {string} params.token - User authentication token
 * @param {Function} params.setIsLoadingDownloadTemplate - Function to set loading state
 * @param {Function} params.setCheckedBasketsIds - Function to clear selected basket IDs
 * @returns {Promise<void>}
 */
export const handleDownloadBulkTemplate = async ({
  allBasketsList,
  checkedBasketsIds,
  token,
  setIsLoadingDownloadTemplate,
  setCheckedBasketsIds,
}) => {
  setIsLoadingDownloadTemplate(true);
  const selectedBaskets = allBasketsList.filter(basket => checkedBasketsIds.includes(basket.id));
  const data = selectedBaskets.map(basket => ({
    symbol: basket.symbol,
    calculation_setup: basket.calculation_setup,
    effective_date: generateEffectiveDate(basket.calculation_setup),
  }));
  const body = { data };

  const indexSymbolToDownload = allBasketsList.filter(item =>
    checkedBasketsIds.includes(item.id),
  )[0]?.symbol;

  try {
    const response = await downloadTemplate(token, body);
    const blob = response.data;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    if (checkedBasketsIds.length > 1) {
      link.setAttribute('download', `Template-Baskets-${getDateString()}.zip`);
    } else if (checkedBasketsIds.length === 1) {
      link.setAttribute(
        'download',
        `Template-Basket-${indexSymbolToDownload}-${getDateString()}.zip`,
      );
    }
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    toast.error(
      'Failed to download template. Please check your network connection or try again later.',
    );
  } finally {
    setCheckedBasketsIds([]);
    setIsLoadingDownloadTemplate(false);
  }
};

/**
 * Creates data to show on summary by comparing current and previous basket data.
 * @param {Array} selectedBasketsData - Current basket data
 * @param {Array} previousData - Previous basket data for comparison
 * @param {string} authOrgPrefix - Organization prefix (e.g., 'UB') to determine field order
 * @returns {Array} Array of baskets with modified meta_reference_data showing changes
 */
export const createDataToShowOnSummary = (selectedBasketsData, previousData, authOrgPrefix) => {
  // Ensure selectedBasketsData and previousData are arrays
  if (!Array.isArray(selectedBasketsData) || !Array.isArray(previousData)) {
    return [];
  }

  // Helper: Build a lookup for meta_reference_data by ISIN for a given basket
  const buildMetaDataMap = meta_reference_data => {
    const map = {};
    if (Array.isArray(meta_reference_data)) {
      meta_reference_data.forEach(item => {
        if (item && typeof item.isin === 'string') {
          map[item.isin] = { ...item };
        }
      });
    }
    return map;
  };

  const allResults = [];

  selectedBasketsData.forEach(basket => {
    // Find the corresponding previous basket by symbol
    const prevBasket = previousData.find(pb => pb?.symbol === basket?.symbol);

    // Defensive: Ensure meta_reference_data is an array
    const currentMeta = Array.isArray(basket?.meta_reference_data)
      ? basket.meta_reference_data
      : [];
    const previousMeta = Array.isArray(prevBasket?.meta_reference_data)
      ? prevBasket.meta_reference_data
      : [];

    // Build lookup maps for current and previous meta_reference_data
    const oldData = buildMetaDataMap(previousMeta);
    const newData = buildMetaDataMap(currentMeta);

    // Track changes for summary (skip items with no_change)
    const modifiedRows = [];

    // Process current items: update, add, or no change
    Object.keys(newData).forEach(isin => {
      const item = newData[isin];
      const oldItem = oldData[isin];

      // Check if MIC and shares changed (only if oldItem exists)
      const micChanged = oldItem ? (oldItem.exchange ?? '') !== (item.exchange ?? '') : false;
      const sharesChanged = oldItem ? (oldItem.shares ?? '') !== (item.shares ?? '') : false;

      // Determine modification type directly
      const type = oldItem
        ? micChanged || sharesChanged
          ? TYPE_OF_MODIFICATION.UPDATE
          : TYPE_OF_MODIFICATION.NO_CHANGE
        : TYPE_OF_MODIFICATION.ADDITION;

      // Skip items with no_change
      if (type === TYPE_OF_MODIFICATION.NO_CHANGE) {
        return;
      }

      // If MIC didn't change, keep it in old and set new to "-"
      // If shares didn't change, keep it in old and set new to "-"
      // For additions, show new values in new columns
      const oldMic = oldItem?.exchange ?? '-';
      const newMic = oldItem ? (micChanged ? item?.exchange ?? '-' : '-') : item?.exchange ?? '-';
      const oldShares = oldItem?.shares ?? '-';
      const newShares = oldItem ? (sharesChanged ? item?.shares ?? '-' : '-') : item?.shares ?? '-';

      // Output data in the requested format (excluding 'no_change')
      const entry =
        authOrgPrefix === ORGANIZATION_PREFIXES.UBS
          ? {
              // SEDOL first, then ISIN
              SEDOL: item?.sedol ?? '-',
              ISIN: item?.isin ?? '-',
              ticker: item?.ticker ?? '-',
              old_mic: oldMic,
              new_mic: newMic,
              old_shares: oldShares,
              new_shares: newShares,
              type,
            }
          : {
              // ISIN first, then SEDOL
              ISIN: item?.isin ?? '-',
              SEDOL: item?.sedol ?? '-',
              ticker: item?.ticker ?? '-',
              old_mic: oldMic,
              new_mic: newMic,
              old_shares: oldShares,
              new_shares: newShares,
              type,
            };

      modifiedRows.push(entry);
    });

    // Process deletions: items in oldData not in newData
    Object.keys(oldData).forEach(isin => {
      if (!newData[isin]) {
        const oldItem = oldData[isin];
        const entry =
          authOrgPrefix === ORGANIZATION_PREFIXES.UBS
            ? {
                SEDOL: oldItem?.sedol ?? '-',
                ISIN: oldItem?.isin ?? '-',
                ticker: oldItem?.ticker ?? oldItem?.isin ?? '-',
                old_mic: oldItem?.exchange ?? '-',
                new_mic: '-',
                old_shares: oldItem?.shares ?? '-',
                new_shares: '-',
                type: TYPE_OF_MODIFICATION.DELETE,
              }
            : {
                ISIN: oldItem?.isin ?? '-',
                SEDOL: oldItem?.sedol ?? '-',
                ticker: oldItem?.ticker ?? oldItem?.isin ?? '-',
                old_mic: oldItem?.exchange ?? '-',
                new_mic: '-',
                old_shares: oldItem?.shares ?? '-',
                new_shares: '-',
                type: TYPE_OF_MODIFICATION.DELETE,
              };
        modifiedRows.push(entry);
      }
    });

    // Only include modifiedRows (update, addition, delete) - skip no_change items
    const result = modifiedRows;

    allResults.push({
      ...basket,
      meta_reference_data: result,
    });
  });

  return Array.isArray(allResults) ? [...allResults] : [];
};

export const getColumnWidth = columnId => {
  if (columnId === 'select') return '20px';
  if (columnId === 'name') return 'clamp(150px, 30vw, 220px)';
  return 'min(100px, 12vw)';
};

/**
 * Transforms error objects into a format suitable for modal display.
 * @param {Object} filteredErrors - Object with structure { [symbol]: { [field]: message } }
 * @returns {Object} Object with structure { [symbol]: [{ module, Property, message }] }
 */
export const transformErrorsForModal = filteredErrors => {
  const transformedErrors = {};
  const symbols = Object.keys(filteredErrors);
  for (let i = 0; i < symbols.length; i += 1) {
    const symbol = symbols[i];
    const errorObj = filteredErrors[symbol];
    transformedErrors[symbol] = [];
    const fields = Object.keys(errorObj);
    for (let j = 0; j < fields.length; j += 1) {
      const field = fields[j];
      const message = errorObj[field];
      const module = FIELDS_TO_MODULES_MAP[field];
      const property = FIELDS_TO_PROPERTIES_MAP[field] || field;

      transformedErrors[symbol].push({
        module,
        property,
        message,
      });
    }
  }

  return transformedErrors;
};

/**
 * Converts a string to Title Case.
 * Replaces underscores and hyphens with spaces, capitalizes the first letter of each word,
 * and lowercases the rest of each word.
 *
 * @param {string} str - The input string to convert.
 * @returns {string} The converted string in Title Case. If input is not a string, returns the input unchanged.
 *
 * @example
 * toTitleCase('example_string-name') // returns 'Example String Name'
 */
export const toTitleCase = str =>
  str && typeof str === 'string'
    ? str
        .replace(/[_-]/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
    : str;

/**
 * Generates a validation error message for required fields with specific identifiers.
 * @param {string} fieldName - The name of the field (e.g., 'Shares', 'Coverage').
 * @param {string} authOrgPrefix - The organization prefix ('UB' for SEDOL, others for ISIN).
 * @param {Array<string>} identifiers - Array of ISINs or SEDOLs that are missing the field.
 * @returns {string} Formatted error message.
 */
export const getRequiredFieldError = (fieldName, authOrgPrefix, identifiers) => {
  const identifierType = authOrgPrefix === ORGANIZATION_PREFIXES.UBS ? 'SEDOL(s)' : 'ISIN(s)';
  return `${fieldName} are required for the following ${identifierType}: ${identifiers.join(', ')}`;
};

/**
 * Determines the priority of a metadata item for sorting purposes.
 * Lower return values indicate higher priority in sorting.
 *
 * Priority Rules:
 *   0 - Items WITHOUT coverage (type !== TYPE_OF_MODIFICATION.WITH_COVERAGE)
 *   1 - Items WITH coverage but missing 'shares' (null, undefined, 0, or empty string)
 *   2 - Items WITH coverage AND valid 'shares'
 *
 * @param {Object} item - The metadata item to evaluate.
 * @param {string} item.type - The type of modification.
 * @param {number|string|null|undefined} item.shares - The shares value.
 * @returns {number} The priority classification (0 = highest, 2 = lowest).
 */
const getPriority = item => {
  const isWithoutCoverage = item?.type !== TYPE_OF_MODIFICATION.WITH_COVERAGE;
  const hasShares =
    item?.shares !== null &&
    item?.shares !== undefined &&
    item?.shares !== 0 &&
    item?.shares !== '';

  // Lower number = higher priority
  if (isWithoutCoverage) return 0;
  if (!hasShares) return 1;
  return 2;
};

/**
 * Sorts an array of metadata objects by their coverage priority.
 *
 * The sorting is based on a priority system determined by the `getPriority` function:
 *   0 - Metadata items WITHOUT coverage (type !== TYPE_OF_MODIFICATION.WITH_COVERAGE)
 *   1 - Metadata items WITH coverage but missing 'shares' (null, undefined, 0, or empty string)
 *   2 - Metadata items WITH coverage AND valid 'shares'
 *
 * Items with lower priority values are sorted before those with higher values.
 * Items with the same priority retain their original order.
 *
 * @param {Array<Object>} metadata - The metadata array to sort.
 * @returns {Array<Object>} A new sorted array of metadata objects.
 */
export const sortMetadataByCoverage = metadata => {
  if (!Array.isArray(metadata) || metadata.length === 0) {
    return Array.isArray(metadata) ? [...metadata] : [];
  }

  return [...metadata].sort((a, b) => {
    const aPriority = getPriority(a);
    const bPriority = getPriority(b);

    if (aPriority < bPriority) return -1;
    if (aPriority > bPriority) return 1;
    return 0; // preserve original order for same priority
  });
};

/**
 * Finds the request-coverage item that corresponds to a constituent.
 * Each ISIN appears once in a basket, so the match is on ISIN only.
 *
 * @param {Object} constituent - Constituent row to match.
 * @param {string} constituent.isin - ISIN used as the key.
 * @param {Array<Object>} [requestCoverage=[]] - Items from the coverage response.
 * @returns {Object|null} The matching coverage item, or null when none matches.
 */
export const findRequestCoverageMatch = (constituent, requestCoverage = []) => {
  return requestCoverage.find(item => item?.isin && item.isin === constituent?.isin) ?? null;
};

/**
 * Builds the reference fields copied from a coverage response item.
 * Null values are included so a missing company name, ticker, or SEDOL replaces the previous value.
 * The patch contains `company_name`, `ticker`, `sedol`, and `type`.
 *
 * @param {Object} coverage - A request-coverage item from the API.
 * @param {string|null} coverage.company_name - Company name from the response.
 * @param {string|null} coverage.ticker - Ticker from the response.
 * @param {string|null} coverage.sedol - SEDOL from the response.
 * @param {string} [coverage.type] - Coverage status, such as `not_found` or `request_coverage`.
 * @returns {{company_name: (string|null), ticker: (string|null), sedol: (string|null), type: string}} Fields to overlay on the constituent.
 */
export const buildRequestedCoverageRow = coverage => ({
  company_name: coverage.company_name,
  ticker: coverage.ticker,
  sedol: coverage.sedol,
  type: coverage.type ?? TYPE_OF_MODIFICATION.REQUEST_COVERAGE,
});

/**
 * Applies coverage reference fields onto a constituent and keeps the rest of the row.
 * AWF, IWF, shares, weights, exchange, and every other existing field stay in place.
 * When no coverage item is provided, the constituent is marked as covered and its reference fields stay unchanged.
 *
 * @param {Object} constituent - Existing constituent row.
 * @param {Object|null} coverage - Matching request-coverage item, or null when the row is covered.
 * @returns {Object} Constituent with updated coverage fields.
 */
const mergeCoverageReferenceFields = (constituent, coverage) => {
  if (!coverage) {
    return {
      ...constituent,
      type: TYPE_OF_MODIFICATION.WITH_COVERAGE,
    };
  }

  return {
    ...constituent,
    ...buildRequestedCoverageRow(coverage),
  };
};

/**
 * Recalculates constituent coverage and writes the result into basket state.
 * Matching rows receive `company_name`, `ticker`, `sedol`, and `type` from `request_coverage`.
 * The merge uses the latest `meta_reference_data` so in-flight edits to shares and factors are kept.
 * A failed request leaves basket state unchanged and shows an error toast.
 *
 * @param {Array<Object>} constituentsData - Constituents sent to the coverage endpoint.
 * @param {string} calculationFrequency - Calculation setup sent with the request (`EOD` or `EOD_INTRADAY`).
 * @param {string} token - Auth token for the coverage request.
 * @param {Function} setData - Basket state setter. Receives an updater of the previous state.
 * @param {Function} [setIsCoverageLoading] - Called with true when the request starts and false when it finishes.
 * @returns {Promise<void>}
 */
export const updateCoverageHelper = async (
  constituentsData,
  calculationFrequency,
  token,
  setData,
  setIsCoverageLoading,
) => {
  if (setIsCoverageLoading) {
    setIsCoverageLoading(true);
  }
  try {
    const constituents = constituentsData.map(item => ({
      isin: item.isin,
      exchange: item?.exchange,
    }));

    const response = await recalculateCoverage(token, {
      constituents,
      calculation_setup: calculationFrequency,
    });

    const requestCoverage = response.data?.data?.request_coverage ?? [];

    setData(prev => ({
      ...prev,
      meta_reference_data: (prev?.meta_reference_data ?? constituentsData).map(constituent =>
        mergeCoverageReferenceFields(
          constituent,
          findRequestCoverageMatch(constituent, requestCoverage),
        ),
      ),
    }));
  } catch (error) {
    console.error(error);
    toast.error(
      'Error updating coverage data. Please try again later or contact support if the issue persists.',
    );
  } finally {
    if (setIsCoverageLoading) {
      setIsCoverageLoading(false);
    }
  }
};

export const isValidExcelFile = fileObj => {
  if (!fileObj) return false;

  const fileName =
    typeof fileObj === 'string'
      ? String(fileObj).toLowerCase()
      : String(fileObj?.name || '').toLowerCase();

  const fileType = typeof fileObj === 'string' ? '' : String(fileObj?.type || '').toLowerCase();

  const validExtensions = ['.csv', '.xls', '.xlsx'];

  const mimeTypes = {
    csv: ['text/csv', 'application/csv', 'text/plain'],
    xls: ['application/vnd.ms-excel'],
    xlsx: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
  };

  const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));

  const allMimeTypes = [...mimeTypes.csv, ...mimeTypes.xls, ...mimeTypes.xlsx];
  const hasValidMimeType = allMimeTypes.some(type => fileType.includes(type));

  return hasValidExtension || hasValidMimeType;
};

export const generateBulkBasketErrorToast = () => {
  toast.error('One or more errors were detected. Please review them in the Basket Errors section.');
};

/**
 * Builds Additional Parameters fields for bulk create/update submit payloads.
 * Cash Distribution and Application Point are only included when the return type
 * is not Price Return and each value is set.
 *
 * @param {Object} basket - Basket data used to build the submit payload
 * @returns {{cash_distribution?: string, application_point?: string}} Fields to spread into the submit body, or `{}` for Price Return
 */
const getAdditionalParametersSubmitFields = basket => {
  if (basket?.calculation_type?.toUpperCase() === PRICE_RETURN) {
    return {};
  }

  const fields = {};
  const cashDistribution = basket?.additional_settings?.m_cash_distribution_type?.name;
  const applicationPoint = basket?.additional_settings?.cash_distribution_application_point;

  if (cashDistribution != null) {
    fields.cash_distribution = cashDistribution;
  }
  if (applicationPoint) {
    fields.application_point = applicationPoint;
  }
  return fields;
};

/**
 * Handles the submission of basket updates and returns the processed baskets data.
 * @param {Object} params - Parameters object
 * @param {Array} params.selectedBasketsData - Array of basket data to update
 * @param {Function} params.existsCashConstituentsInMetadata - Function to check if cash constituents exist
 * @param {string} params.token - User authentication token
 * @returns {Promise<Array|null>} Array of processed baskets with symbol, message, and status, or null if error
 */
export const updateSubmitBulkBasket = async ({
  selectedBasketsData,
  existsCashConstituentsInMetadata,
  token,
}) => {
  const body = {
    data: selectedBasketsData.map(basket => {
      const {
        metadata_effective_date,
        asset_class,
        type,
        calculation_frequency,
        calculation_formula,
        m_index_eod_execution_group,
        count_reference_data,
        count_metadata,
        id_m_index_parent,
        bloomberg_id,
        additional_settings,
        meta_reference_data,
        effective_date,
        automatically_adjust_divisor,
        intraday,
        launch_date,
        eod_calculation_time,
        divisor,
        ...filteredRest
      } = basket;
      const divisorFields = {};
      if (basket?.automatically_adjust_divisor) {
        divisorFields.automatically_adjust_divisor = basket.automatically_adjust_divisor;
      } else if (divisor) {
        divisorFields.divisor = Number(divisor);
        divisorFields.divisor_determination_setup = null;
      }
      return {
        basket_update: {
          ...filteredRest,
          ...divisorFields,
          metadata_effective_date:
            basket?.effective_date ?? generateEffectiveDate(basket?.calculation_setup),
          ...getAdditionalParametersSubmitFields(basket),
          calculation_type: basket.calculation_type.toUpperCase(),
          calculation_zone: basket.calculation_zone.toUpperCase(),
          calculation_setup: basket.calculation_setup,
          metadata: basket.meta_reference_data,
          cash_constituent: existsCashConstituentsInMetadata(basket?.symbol)
            ? {
                interest_rate_on_cash:
                  Number(basket?.additional_settings?.cash_constituent?.interest_rate_on_cash) || 0,
              }
            : null,
        },
      };
    }),
  };
  try {
    const bulkResponse = await updateBulkBasket(token, body);
    const processedBasketsList = Object.entries(bulkResponse?.data?.data).map(
      ([symbol, basket]) => ({
        symbol,
        message: basket?.basket_update?.message,
        status: basket?.basket_update?.status,
      }),
    );
    return processedBasketsList;
  } catch (error) {
    toast.error(
      'Failed to update basket. Please check your network connection or try again later.',
    );
    return null;
  }
};

/**
 * Handles the submission of basket creation and returns the processed baskets data.
 * @param {Object} params - Parameters object
 * @param {Array} params.selectedBasketsData - Array of basket data to create
 * @param {Object} params.enableDivisorOrStartingValue - Object mapping symbols to divisor/starting value status
 * @param {boolean} params.shouldSendLaunchDate - Whether to include launch_date in the submission
 * @param {Function} params.existsCashConstituentsInMetadata - Function to check if cash constituents exist
 * @param {string} params.token - User authentication token
 * @returns {Promise<Array|null>} Array of processed baskets with symbol, message, and status, or null if error
 */
export const createSubmitBulkBasket = async ({
  selectedBasketsData,
  enableDivisorOrStartingValue,
  shouldSendLaunchDate,
  existsCashConstituentsInMetadata,
  token,
}) => {
  const body = {
    data: selectedBasketsData.map(basket => {
      const divisorOrStartingValue = removeNulls({
        divisor: enableDivisorOrStartingValue?.[basket?.symbol]?.statusDivisor
          ? Number(basket?.divisor)
          : null,
        starting_value: enableDivisorOrStartingValue?.[basket?.symbol]?.statusStarting
          ? Number(basket?.starting_value)
          : null,
        divisor_determination_setup: enableDivisorOrStartingValue?.[basket?.symbol]?.statusStarting
          ? basket?.divisor_determination_setup
          : null,
      });

      const LaunchDateData = shouldSendLaunchDate ? { launch_date: basket?.launch_date } : {};

      const LaunchPointData =
        basket?.calculation_setup === DATA_CALCULATION_SETUP_VALUES.EOD_INTRADAY
          ? { launch_point: basket?.launch_point }
          : {};

      const additionalParametersFields = getAdditionalParametersSubmitFields(basket);

      return {
        basket_submission: {
          ...LaunchDateData,
          ...LaunchPointData,
          ...divisorOrStartingValue,
          symbol: basket?.symbol,
          name: basket?.name,
          description: basket?.description,
          currency: basket?.currency,
          org_prefix: basket?.org_prefix ?? '',
          metadata: basket?.meta_reference_data,
          calculation_type: basket.calculation_type.toUpperCase(),
          calculation_zone: basket.calculation_zone.toUpperCase(),
          withholding_tax_id: basket?.withholding_tax_id ?? null,
          ...additionalParametersFields,
          corporate_actions_methodology: 'BITA',
          calculation_start_week_day: Number(basket?.calculation_start_week_day),
          calculation_end_week_day: Number(basket?.calculation_end_week_day),
          calculation_open_time: basket?.calculation_open_time,
          calculation_close_time: basket?.calculation_close_time,
          calculation_eod_time: basket?.calculation_eod_time,
          timezone: basket?.timezone,
          calculation_setup: basket?.calculation_setup,
          cash_constituent: existsCashConstituentsInMetadata(basket?.symbol)
            ? {
                interest_rate_on_cash:
                  Number(basket?.additional_settings?.cash_constituent?.interest_rate_on_cash) || 0,
              }
            : null,
          decrement_basket: {
            annualized_daily_decrement:
              basket?.additional_settings?.annualized_daily_decrement || false,
            decrement_value_percentage:
              basket?.additional_settings?.decrement_value_percentage || 0,
          },
        },
      };
    }),
  };
  try {
    const bulkResponse = await createBulkBasket(token, body);
    const processedBasketsList = Object.entries(bulkResponse?.data?.data).map(
      ([symbol, basket]) => ({
        symbol,
        message: basket?.basket_submission?.message,
        status: basket?.basket_submission?.status,
      }),
    );
    return processedBasketsList;
  } catch (error) {
    toast.error(
      'Failed to update basket. Please check your network connection or try again later.',
    );
    return null;
  }
};

/**
 * Handles editing/loading baskets by fetching their data and transforming it.
 * @param {Object} params - Parameters object
 * @param {Array} params.allBasketsList - Array of all available baskets
 * @param {Array} params.checkedBasketsIds - Array of selected basket IDs to load
 * @param {Array} params.selectedBasketsData - Array of currently selected basket data (to check for duplicates)
 * @param {string} params.token - User authentication token
 * @param {Function} params.setIsLoadingBasketData - Function to set loading state
 * @param {Function} params.setCheckedBasketsIds - Function to clear selected basket IDs
 * @returns {Promise<{updatedResults: Array|null, hasDuplicates: boolean}>} Updated basket results and duplicate flag
 */
export const handleEditBaskets = async ({
  allBasketsList,
  checkedBasketsIds,
  selectedBasketsData,
  token,
  setIsLoadingBasketData,
  setCheckedBasketsIds,
}) => {
  setIsLoadingBasketData(true);
  try {
    const selectedIndexes = allBasketsList.filter(item => checkedBasketsIds.includes(item.id));
    // do not allow duplicated symbols on previous selected baskets
    const missingIds = selectedIndexes
      .map(item => item.symbol)
      .filter(symbol => selectedBasketsData.some(basket => basket.symbol.trim() === symbol.trim()));
    if (missingIds.length > 0) {
      toast.error(
        `The following selected basket symbols are duplicated : ${missingIds.join(', ')}`,
      );
      setIsLoadingBasketData(false);
      return { updatedResults: null, hasDuplicates: true };
    }
    const results = [];
    await Promise.all(
      selectedIndexes.map(async item => {
        const resp = await getBasketById(token, item.symbol);
        if (resp?.data?.data && resp.data.data.symbol) {
          results.push(resp.data.data);
          toast.success(`Basket processed successfully: ${item.symbol}`);
        } else {
          toast.error(`Error: Basket data is empty for: ${item.symbol}`);
        }
      }),
    );
    // Add withholding_tax_id key to each basket in results if possible
    const updatedResults = results.map(basket => {
      const { calculation_frequency, ...filteredBasket } = basket;
      return {
        ...filteredBasket,
        withholding_tax_id: basket?.additional_settings?.withholding_tax_id ?? null,
        divisor_determination_setup:
          basket?.additional_settings?.divisor_determination_setup ?? 'PREVIOUS_EOD',
        calculation_type: basket?.calculation_type?.toUpperCase(),
        effective_date: generateEffectiveDate(calculation_frequency),
        automatically_adjust_divisor: !basket?.divisor,
        calculation_setup: calculation_frequency,
        meta_reference_data: sortMetadataByCoverage(basket?.meta_reference_data),
        calculation_zone: basket?.additional_settings?.m_index_calculation_zone?.name,
        additional_settings: {
          ...basket?.additional_settings,
          cash_distribution_application_point:
            basket?.additional_settings?.cash_distribution_application_point ?? null,
        },
      };
    });
    return { updatedResults, hasDuplicates: false };
  } catch (error) {
    console.error('Error loading basket data:', error);
    return { updatedResults: null, hasDuplicates: false };
  } finally {
    setIsLoadingBasketData(false);
    setCheckedBasketsIds([]);
  }
};

export const handleCalculationZones = async token => {
  try {
    const rsp = await getCalculationZones(token);
    const data = rsp?.data?.data ?? [];
    return data;
  } catch (error) {
    console.error('Error fetching calculation zones:', error);
    throw error; // Re-throw so the component can handle it
  }
};

export const getZoneValue = (calculationZoneMap, selectedBasket, key, isDay = false) => {
  const config = calculationZoneMap.get(selectedBasket?.calculation_zone)?.config;
  if (!config || config[key] === undefined) return '-';
  return isDay ? DAYS_OF_THE_WEEK[config[key]] : config[key];
};

/**
 * Normalizes a raw value for safe display in a preview table cell.
 * Rejects nullish, empty, and whitespace-only strings, returning a "-" placeholder instead.
 *
 * @param {unknown} value - The value to normalize.
 * @returns {string} The trimmed string when valid, otherwise '-'.
 *
 * @example
 * normalizePreviewValue('  hello  ') // 'hello'
 * normalizePreviewValue('')          // '-'
 * normalizePreviewValue(null)        // '-'
 * normalizePreviewValue(123)         // '-'
 */
export const normalizePreviewValue = value =>
  typeof value === 'string' && value.trim() !== '' ? value.trim() : '-';

/**
 * Type guard that decides whether a raw value can be rendered inside a preview table cell.
 * Accepts non-empty strings and finite-ish numbers (anything but NaN); rejects null, undefined,
 * empty/whitespace-only strings, booleans, objects, and arrays.
 *
 * @param {unknown} value - The value to test.
 * @returns {boolean} True when the value is safe to render as-is.
 *
 * @example
 * isPreviewValueRenderable('hello') // true
 * isPreviewValueRenderable(0)       // true
 * isPreviewValueRenderable('   ')   // false
 * isPreviewValueRenderable(NaN)     // false
 * isPreviewValueRenderable(null)    // false
 */
export const isPreviewValueRenderable = value =>
  (typeof value === 'string' && value.trim() !== '') ||
  (typeof value === 'number' && !Number.isNaN(value));

/**
 * Normalizes a raw value for the Parameters preview table, where cells may hold either
 * strings or numbers. Trims and replaces underscores with spaces for strings, returns numbers
 * untouched, and falls back to either the "BITA Withholding Taxes" label (for the withholding
 * tax row) or a "-" placeholder when the value is not renderable.
 *
 * @param {unknown} value - The value to normalize.
 * @param {boolean} [isWithholdingTaxTemplate=false] - True when the cell belongs to the
 *   withholding tax row, which uses a different empty-state placeholder.
 * @returns {string | number} The cleaned value ready to render.
 *
 * @example
 * normalizePreviewDisplayValue('EOD_INTRADAY')        // 'EOD INTRADAY'
 * normalizePreviewDisplayValue(0)                     // 0
 * normalizePreviewDisplayValue(null)                  // '-'
 * normalizePreviewDisplayValue(null, true)            // 'BITA Withholding Taxes'
 */
export const normalizePreviewDisplayValue = (value, isWithholdingTaxTemplate = false) => {
  if (isPreviewValueRenderable(value)) {
    return typeof value === 'string' ? value.trim().replace(/_/g, ' ') : value;
  }
  return isWithholdingTaxTemplate ? 'BITA Withholding Taxes' : '-';
};

/**
 * Computes the diff metadata for a single preview row when comparing a current row
 * against its previous counterpart. Used by the Old Value / Modified Value tables
 * inside the basket update preview modal.
 *
 * @param {{ value: unknown }} row - The current row (must expose a `value` field).
 * @param {{ value: unknown } | undefined | null} previousRow - The previous row aligned by index, or null.
 * @param {boolean} showOldValueColumn - Whether the diff column is being rendered.
 * @returns {{ displayValue: string, previousDisplayValue: string | null, hasChanged: boolean }}
 *   - `displayValue`: normalized current value.
 *   - `previousDisplayValue`: normalized previous value, or null when the diff column is hidden.
 *   - `hasChanged`: true when both sides are visible and their stringified values differ.
 */
export const buildPreviewRowDiff = (row, previousRow, showOldValueColumn) => {
  const displayValue = normalizePreviewValue(row?.value);
  const previousDisplayValue = showOldValueColumn
    ? normalizePreviewValue(previousRow?.value)
    : null;
  const hasChanged = showOldValueColumn && String(previousDisplayValue) !== String(displayValue);
  return { displayValue, previousDisplayValue, hasChanged };
};

export const buildAdditionalParametersRows = settings => [
  {
    label: 'Corporate Actions Methodology',
    value: 'BITA CORPORATE ACTIONS METHODOLOGY',
  },
  {
    label: 'Cash Distribution Application Point',
    value: settings?.cash_distribution_application_point?.replace(/_/g, ' ') || '-',
  },
  {
    label: 'Cash Distribution Type',
    value: settings?.m_cash_distribution_type?.name?.replace(/_/g, ' ') || '-',
  },
  {
    label: 'Dissemination Options',
    value: 'BITA',
  },
];

export const buildAdditionalParametersPreviewRows = (rows, previousRows, calculationType) => {
  const showOldValueColumn = previousRows != null;
  const getPreviousRow = label => previousRows?.find(row => row.label === label);
  return rows
    .filter(
      row =>
        !(
          calculationType === PRICE_RETURN &&
          (row.label === CASH_DISTRIBUTION_TYPE_LABEL ||
            row.label === CASH_DISTRIBUTION_APPLICATION_POINT_LABEL)
        ),
    )
    .map(row => ({
      label: row.label,
      ...buildPreviewRowDiff(row, getPreviousRow(row.label), showOldValueColumn),
    }));
};

/**
 * Truncates a string to a maximum length and appends an ellipsis when trimmed.
 *
 * @param {string} str - The string to truncate.
 * @param {number} limit - The maximum number of characters to keep before the ellipsis.
 * @returns {string} The truncated string with a trailing ellipsis when over the limit, otherwise the original string unchanged.
 *
 * @example
 * truncatePreviewString('hello world', 5) // 'hello...'
 * truncatePreviewString('hi', 5)          // 'hi'
 */
export const truncatePreviewString = (str, limit) => {
  if (str.length > limit) {
    return `${str.substring(0, limit)}...`;
  }
  return str;
};

/**
 * Resolves a Parameters preview field key into its human-readable label.
 * Falls back to the raw key when no mapping exists in `SUMMARY_BULK_PREVIEW_LABELS`.
 *
 * @param {string} item - The field key (e.g. `'calculation_setup'`).
 * @returns {string} The mapped label, or the original key when not mapped.
 */
export const getPreviewLabel = item => SUMMARY_BULK_PREVIEW_LABELS[item] || item;

/**
 * Formats a Date-parseable input into a `yyyy-mm-dd` string using the local timezone.
 * Note: returns 'NaN-NaN-NaN' if the input is not parseable (matches legacy behavior).
 *
 * @param {string | number | Date} dateString - A value accepted by the Date constructor.
 * @returns {string} The formatted date string `yyyy-mm-dd`.
 *
 * @example
 * formatPreviewDateString('2026-01-09') // '2026-01-09'
 */
export const formatPreviewDateString = dateString => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${year}-${month}-${day}`;
};

/**
 * Decides whether a Parameters preview field key should be rendered for the current basket.
 * Encodes the domain rules around excluded keys, withholding tax visibility, divisor visibility,
 * cash-constituent presence, and annualized daily decrement.
 *
 * @param {string} item - The field key being considered.
 * @param {string} calculationType - The basket's calculation/return type (e.g. `'NET_RETURN'`).
 * @param {boolean} automaticallyAdjustDivisor - True when auto-adjust is enabled (shows divisor
 *   determination setup, hides manual divisor).
 * @param {boolean} existsCashConstituentsInMetadata - True when the basket has cash constituents.
 * @param {boolean} isAnnualizedDailyDecrement - True when the basket is an annualized daily decrement.
 * @returns {boolean} True when the row should be rendered.
 */
export const shouldRenderPreviewItem = (
  item,
  calculationType,
  automaticallyAdjustDivisor,
  existsCashConstituentsInMetadata,
  isAnnualizedDailyDecrement,
  divisorDeterminationSetup,
) => {
  if (EXCLUDED_ITEMS.has(item)) return false;
  if (item === 'withholding_tax_id' && calculationType !== 'NET_RETURN') return false;
  if (item === 'divisor' && automaticallyAdjustDivisor === true) return false;
  if (
    item === 'divisor_determination_setup' &&
    automaticallyAdjustDivisor === false &&
    !divisorDeterminationSetup
  )
    return false;
  if (item === 'interest_rate_on_cash' && !existsCashConstituentsInMetadata) return false;
  if (item === 'decrement_value_percentage' && !isAnnualizedDailyDecrement) return false;
  return true;
};

/**
 * Formats a row label for the Parameters preview table.
 * Title-cases string labels, returns `'-'` when missing, and passes non-string labels through.
 *
 * @param {string | null | undefined | unknown} label - The raw row label.
 * @returns {string | unknown} The display-ready label.
 */
export const formatPreviewRowLabel = label => {
  if (!label) return '-';
  return typeof label === 'string' ? toTitleCase(label) : label;
};

/**
 * Case-insensitive check for the withholding-tax-template row label, which uses a different
 * empty-state placeholder than other rows.
 *
 * @param {unknown} label - The raw row label.
 * @returns {boolean} True when the label is the withholding tax template row.
 */
export const isWithholdingTaxTemplateLabel = label =>
  typeof label === 'string' && label.toLowerCase() === 'withholding tax template';

/**
 * Resolves a raw Parameters preview field into its display-ready value, applying per-field
 * formatting (description truncation, date formatting, calculation frequency lookup,
 * ticker prefixing, withholding tax template lookup, decrement percentage formatting).
 *
 * @param {string} item - The field key being rendered (e.g. `'calculation_setup'`).
 * @param {unknown} value - The raw value from the basket data.
 * @param {Object} context - Per-render dependencies needed for some fields.
 * @param {string} [context.launchDate] - The launch date used by the `'launch_date'` field.
 * @param {string} [context.orgPrefix] - The organization prefix prepended to the `'symbol'` field.
 * @param {Array<{ id: string, withholding_tax_name: string }>} [context.withholdingTaxTemplateData] -
 *   Templates used to resolve the `'withholding_tax_id'` field into a human-readable name.
 * @returns {unknown} The display-ready value (the raw value when no special-case applies).
 */
export const getPreviewItemDisplayValue = (item, value, context = {}) => {
  const { launchDate, orgPrefix = '', withholdingTaxTemplateData } = context;
  switch (item) {
    case 'description':
      return truncatePreviewString(value, 80) ?? '-';
    case 'calculation_zone':
      return value ?? '-';
    case 'launch_date':
      return formatPreviewDateString(launchDate) ?? '-';
    case 'calculation_setup':
      return CALCULATION_FREQUENCY_MAPPING[value] ?? '-';
    case 'dissemination':
      if (value === 'bita_bloomberg') return 'BITA + Bloomberg';
      if (value === 'bita_only') return 'BITA Only';
      return value;
    case 'symbol':
      return orgPrefix + value;
    case 'withholding_tax_id':
      return value
        ? withholdingTaxTemplateData?.find(tax => tax.id === value)?.withholding_tax_name
        : 'BITA Withholding Taxes';
    case 'decrement_value_percentage':
      return `${value * 100}%`;
    default:
      return value;
  }
};

/**
 * Builds the fully-resolved display row for the Parameters preview table.
 * Combines label formatting, withholding-tax detection, current/previous value normalization,
 * and the change comparison into a single flat object consumed by the JSX.
 *
 * @param {{ key?: string, label?: unknown, value?: unknown, previousValue?: unknown }} row -
 *   The intermediate row (from the upstream `displayData` builder).
 * @param {boolean} showOldValueColumn - Whether the diff column is being rendered.
 * @returns {{ key: string | undefined, displayLabel: string | unknown, displayValue: string | number, previousDisplayValue: string | number | null, hasChanged: boolean }}
 */
export const buildPreviewDisplayRow = (row, showOldValueColumn) => {
  const displayLabel = formatPreviewRowLabel(row.label);
  const isWithholdingTaxTemplate = isWithholdingTaxTemplateLabel(row.label);
  const displayValue = row.preserveRawValue
    ? normalizePreviewValue(row.value)
    : normalizePreviewDisplayValue(row.value, isWithholdingTaxTemplate);
  const previousDisplayValue = showOldValueColumn
    ? row.preserveRawValue
      ? normalizePreviewValue(row.previousValue)
      : normalizePreviewDisplayValue(row.previousValue, isWithholdingTaxTemplate)
    : null;
  const hasChanged = showOldValueColumn && String(previousDisplayValue) !== String(displayValue);
  return {
    key: row.key,
    displayLabel,
    displayValue,
    previousDisplayValue,
    hasChanged,
    isNestedRow: row.isNestedRow,
  };
};
